<template>
  <Loader
    :value="promises.fetchOne"
    class="project-mutate view"
    :data-project-theme="$path('theme', item)"
    :message="$metaInfo.title"
  >
    <div class="container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #title>
          <div class="row middle">
            <h1 class="header__title">{{ $metaInfo.title }}</h1>
            <img
              v-if="($store.state.enums.UsedProjectLanguages || []).length > 1"
              class="ml-2"
              :src="`/images/flags/${item.metadata.language}.svg`"
              width="20"
            />
          </div>
        </template>
        <template #actions>
          <ButtonComponent tag="a" :href="item.url" target="_blank" :label="$t('preview')" :icon="['fal', 'eye']" />
          <Actions placement="project" :query="{ project: id }" />
          <ButtonComponent
            :label="saveState.label"
            :success-label="$t('saved')"
            :loading-label="$t('saving')"
            :icon="['fal', saveState.icon]"
            theme="primary"
            style="margin-right: auto"
            :promise="promises.mutate"
            :class="{
              'elder-button--disabled': saveState.disabled,
              'elder-button--invalid': saveState.invalid,
            }"
            v-tooltip="{ content: saveState.tooltip }"
            @click="submit"
          />
        </template>
      </HeaderComponent>
      <StepperComponent :value="steps" ref="stepper" state-handler="route" transition="fade" @submit="submit">
        <template #breadcrumb="{ item, navigate, index }">
          <button
            type="button"
            class="elder-stepper__breadcrumb-button"
            :class="{ 'elder-stepper__breadcrumb-button--invalid': !states.fetchOne && !item.isValid }"
            @click="navigate"
          >
            <span class="elder-stepper__breadcrumb-icon">
              <template v-if="states.fetchOne || item.isValid">{{ index + 1 }}</template>
              <FontAwesomeIcon v-else :icon="['fas', 'exclamation-triangle']" size="sm" />
            </span>
            <span>{{ item.label }}</span>
          </button>
        </template>
        <template #after="{ meta, next, prev }">
          <div class="project-mutate__navigation">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              type="submit"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              type="submit"
              :icon="['fal', 'angle-right']"
              :disabled="!validator.passes"
              @click="onNavigation(next)"
            />
            <ButtonComponent
              v-if="!meta.next"
              :label="$t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="['fal', 'arrow-to-right']"
              theme="success"
              type="button"
              :disabled="!validator.passes"
              :promise="promises.mutate"
              @click="complete"
            />
          </div>
        </template>
      </StepperComponent>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="onRemoved"
        :resource="$tc('project')"
      />
    </div>
  </Loader>
</template>

<script>
import Model from '@/models/Project/base'
import ValidatorMixin from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import { mergeDeepRight } from 'ramda'
import Bus from '@/bus'
import { buildStepsFields } from '@/utils'

import { StepperComponent } from 'vue-elder-stepper'
import { DropdownComponent } from 'vue-elder-dropdown'
import HeaderComponent from '@/components/Header'
import RemoveComponent from '@/components/Project/Remove'
import { getValidatorConfig } from '@kvass/custom-fields'
import Steps from './Steps'
import Actions from '@/components/Actions'
import fields from '@/components/Project/StepsFields/fields.js'
import { getMediaTypes } from '@kvass/media-types'

export default {
  mixins: [
    ValidatorMixin({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      source: 'item',
      model: Model,
      guard: true,
      rules: [{ onKey: 'publishing.relation', compare: 'ref' }],
      transform: {
        'customFieldsConfig.*.schema': undefined,
      },
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
    }
  },
  watch: {
    id: {
      handler(id) {
        return this.fetch({ id }).then(() => {
          if (!this.item.id) return this.$router.replace({ name: 'notfound' })
        })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Project', ['item', 'promises', 'states']),
    types: () => getMediaTypes(),
    labels() {
      return {
        ...mapi18n({
          'media.logo': 'logo',
          'media.logoInverted': 'logoInverted',
          favicon: 'favicon',
          description: 'description',
          address: 'address',
          gallery: 'gallery',
          name: 'projectName',
          brochure: 'brochure',
          bidContract: 'bidContract',
          cover: 'cover',
          manager: 'projectmanager',
          salesman: 'customermanager',
          complaintmanager: 'complaintmanager',
          signingAuthority: 'signingAuthority',
          from: 'publishedFrom',
          to: 'publishedTo',
          attachments: ['attachment', 2],
          'publishing.domain': 'domain',
          'publishing.redirectUrl': 'redirectUrl',
        }),
        ...this.customFieldValidatorConfig.labels,
      }
    },
    rules() {
      let rules = {
        name: 'required',
        'publishing.domain': `domain|domainAvailable:${this.item.id}`,
        'publishing.redirectUrl': 'domain',
        ...this.customFieldValidatorConfig.rules,
      }

      let billingRules = {
        'billing.company': 'required',
        'billing.organizationNumber': 'required',
        'billing.email': 'required',
      }

      if (this.data && this.data.billing) return { ...rules, ...billingRules }
      return rules
    },
    addons() {
      return this.$path('metadata.addons', this.item) || []
    },
    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}
      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },
    customFieldSchemas() {
      return (this.$path('data.customFieldsConfig') || [])
        .map(c => c.schema.map(e => ({ ...e, placement: c.placement || e.placement })))
        .flat()
    },
    breadcrumb() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$metaInfo.title,
          action: { name: 'project-dashboard', params: { id: this.id } },
          main: true,
        },
      ]
    },
    saveState() {
      let base = {
        label: this.$t('save'),
        icon: 'arrow-to-bottom',
        disabled: !this.validator.passes || !this.hasChanges,
      }

      if (!this.states.fetchOne && !this.validator.passes)
        return {
          ...base,
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('\n'),
        }

      if (!this.hasChanges)
        return {
          ...base,
          tooltip: this.$t('noUnsavedChanges'),
        }

      return base
    },
    steps() {
      let props = {
        value: this.data,
        data: mergeDeepRight(this.item, this.data),
        item: this.item,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        customFieldSchemas: this.customFieldSchemas,
        fields: buildStepsFields(Array.from(fields.values()), this.customFieldSchemas, this),
      }

      let listeners = {
        submit: () => this.submit(),
      }

      return Steps(this.item)
        .filter(e => {
          switch (e.id) {
            case 'shops':
              return (
                this.$store.getters['Project/hasRole'](/^shop/) &&
                !this.$store.state.enums.DisabledFeatures.includes('project:shop')
              )
            case 'sale':
              return !this.$store.state.enums.DisabledFeatures.includes('project:bid')
            default:
              return true
          }
        })
        .map(raw => {
          const { icon, ...s } = raw
          let errors = Object.keys(this.validator.errors.errors)
          s.isValid = (s.fields || []).every(f => !errors.find(e => e.startsWith(f)))
          s.props = {
            ...(s.props || {}),
            ...props,
            fields: {
              ...props.fields,
              default: props.fields.default.filter(i => i.defaultPlacement === raw.id),
            },
          }
          s.listeners = {
            ...(s.listeners || {}),
            ...listeners,
          }
          return s
        })
    },
  },
  methods: {
    ...mapActions('Project', ['fetch', 'mutate', 'remove']),
    submit() {
      if (!this.hasChanges || !this.validator.passes) return Promise.resolve()

      const data = this.getData({ diff: true })
      const getConfig = 'theme' in data

      return this.mutate([
        {
          id: this.id,
          data,
          getConfig,
        },
        {
          strategy: (item, payload) => {
            const { metadata, customFieldsConfig, url } = payload
            return mergeDeepRight(
              item,
              mergeDeepRight(this.data, getConfig ? { metadata, customFieldsConfig, url } : { url }),
            )
          },
          transformPayload: false,
        },
      ]).then(() => {
        this.$bus.$emit('actions:refetch', 'project')
      })
    },
    async complete() {
      await this.submit()
      this.$router.push({ name: 'project-dashboard', params: { id: this.id } })
    },
    onRemoved() {
      this.resetData()
      this.$router.push({ name: 'projects' })
    },
    chat(value, tag) {
      Bus.$emit('chat', `${tag ? tag + ' - ' : ''}${this.$t('upsaleRequestTemplate', [value])} - ${this.item.name}`)
    },
    onNavigation(callback) {
      callback()
      this.submit()
    },
  },
  metaInfo() {
    return {
      title: this.item.name || `${this.$t('loading', { resource: this.$tc('project').toLowerCase() })}...`,
    }
  },
  components: {
    HeaderComponent,
    StepperComponent,
    RemoveComponent,
    DropdownComponent,
    Actions,
  },
}
</script>

<style lang="scss">
.project-mutate {
  background-color: $background-grey;
  display: flex;

  &__navigation {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }
}
.elder-stepper__component > * {
  margin: 2rem auto;
}
</style>
